<template>
  <div class="customer-folder-portal">
    <CRow>
      <CCol md="4" lg="3">
        <CCard class="h-100">
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <span>Customer Folders</span>
            <CButton size="sm" color="light" @click="loadFolders()">Refresh</CButton>
          </CCardHeader>
          <CCardBody>
            <CInput
              v-model="search"
              placeholder="Search customer number or name"
              @keyup.enter="applySearch"
            >
              <template #append>
                <CButton color="primary" @click="applySearch">Search</CButton>
              </template>
            </CInput>

            <div v-if="foldersLoading" class="portal-state">Loading folders...</div>
            <div v-else-if="!folders.length" class="portal-state">No customer folders found.</div>
            <div v-else class="folder-list">
              <button
                v-for="folder in folders"
                :key="folder.id"
                type="button"
                class="folder-list-item"
                :class="{ active: currentFolder && currentFolder.id === folder.id }"
                @click="openFolder(folder.id)"
              >
                <div class="folder-list-item__name">{{ folder.name }}</div>
                <div class="folder-list-item__meta">
                  <span>{{ folder.customer_number }}</span>
                  <span>{{ folder.file_count }} files</span>
                </div>
              </button>
            </div>

            <div class="folder-pagination" v-if="meta.last_page > 1">
              <CButton size="sm" color="secondary" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">
                Prev
              </CButton>
              <span>Page {{ meta.current_page }} / {{ meta.last_page }}</span>
              <CButton size="sm" color="secondary" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">
                Next
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="8" lg="9">
        <CCard>
          <CCardHeader class="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <div class="portal-title">{{ currentFolder ? currentFolder.name : 'Select a customer folder' }}</div>
              <div class="portal-breadcrumbs" v-if="breadcrumbs.length">
                <button
                  v-for="(crumb, index) in breadcrumbs"
                  :key="crumb.id"
                  type="button"
                  class="breadcrumb-button"
                  @click="openFolder(crumb.id)"
                >
                  {{ crumb.name }}<span v-if="index < breadcrumbs.length - 1"> / </span>
                </button>
              </div>
            </div>
            <div class="portal-actions">
              <CBadge color="info" v-if="currentFolder">{{ currentFolder.file_count }} files</CBadge>
              <CBadge color="secondary" v-if="currentFolder" class="ml-2">{{ currentFolder.subfolder_count }} folders</CBadge>
              <CButton
                size="sm"
                color="primary"
                class="ml-2"
                :disabled="!currentFolder || exportSubmitting"
                @click="queueExport"
              >
                {{ exportSubmitting ? 'Queueing...' : 'Export ZIP' }}
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody>
            <div v-if="folderLoading" class="portal-state">Loading folder...</div>
            <div v-else-if="!currentFolder" class="portal-state">Choose a customer folder to browse files.</div>
            <div v-else>
              <CRow>
                <CCol lg="7">
                  <div class="section-title">Subfolders</div>
                  <div v-if="!subfolders.length" class="portal-empty">No subfolders.</div>
                  <div v-else class="content-grid">
                    <button
                      v-for="folder in subfolders"
                      :key="folder.id"
                      type="button"
                      class="content-folder"
                      @click="openFolder(folder.id)"
                    >
                      <div class="content-folder__icon">📁</div>
                      <div class="content-folder__name">{{ folder.name }}</div>
                      <div class="content-folder__meta">{{ folder.file_count }} files</div>
                    </button>
                  </div>
                </CCol>

                <CCol lg="5">
                  <div class="section-title d-flex justify-content-between align-items-center">
                    <span>Exports</span>
                    <CButton size="sm" color="light" @click="loadExports()">Refresh</CButton>
                  </div>
                  <div v-if="exportsLoading" class="portal-empty">Loading exports...</div>
                  <div v-else-if="!exports.length" class="portal-empty">No exports yet.</div>
                  <div v-else class="exports-list">
                    <div v-for="item in exports" :key="item.id" class="export-card">
                      <div class="export-card__top">
                        <div>
                          <div class="export-card__name">{{ item.download_name || item.export_name }}</div>
                          <small>#{{ item.id }} · {{ formatDate(item.created_at) }}</small>
                        </div>
                        <CBadge :color="statusColor(item.status)">{{ item.status }}</CBadge>
                      </div>
                      <div class="export-card__meta">
                        <span v-if="item.folder">{{ item.folder.name }}</span>
                        <span v-if="item.total_files !== null">{{ item.processed_files }}/{{ item.total_files }} files</span>
                      </div>
                      <div v-if="item.error_message" class="export-card__error">{{ item.error_message }}</div>
                      <CButton
                        v-if="item.status === 'completed'"
                        size="sm"
                        color="success"
                        @click="downloadExport(item)"
                      >
                        Download ZIP
                      </CButton>
                    </div>
                  </div>
                </CCol>
              </CRow>

              <div class="section-title mt-4">Files</div>
              <div v-if="!files.length" class="portal-empty">No files in this folder.</div>
              <div v-else class="files-table">
                <table class="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="file in files" :key="file.id">
                      <td class="file-preview">
                        <img
                          v-if="file.id && file.artwork_available !== false"
                          :src="previewSrc(file)"
                          :alt="file.name"
                          class="preview-image"
                          @click="openPreview(file)"
                        />
                        <span v-else class="text-muted small">Artwork unavailable</span>
                      </td>
                      <td>{{ file.name }}</td>
                      <td>{{ file.mime_type || 'file' }}</td>
                      <td>{{ formatBytes(file.size) }}</td>
                      <td>{{ formatDate(file.updated_at) }}</td>
                      <td class="text-right">
                        <CButton
                          size="sm"
                          color="info"
                          :disabled="file.artwork_available === false"
                          @click="downloadFile(file)"
                        >Download</CButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="folder-pagination" v-if="filesTotal > fileLimit">
                <CButton size="sm" color="secondary" :disabled="fileOffset <= 0" @click="changeFileOffset(fileOffset - fileLimit)">
                  Prev
                </CButton>
                <span>{{ fileOffset + 1 }}-{{ Math.min(fileOffset + fileLimit, filesTotal) }} of {{ filesTotal }}</span>
                <CButton size="sm" color="secondary" :disabled="fileOffset + fileLimit >= filesTotal" @click="changeFileOffset(fileOffset + fileLimit)">
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CModal
      title="Image Preview"
      size="xl"
      :show.sync="previewModal"
    >
      <div v-if="previewFile" class="preview-modal">
        <div class="preview-modal__header">
          <strong>{{ previewFile.name }}</strong>
        </div>
        <img :src="fullImageSrc(previewFile)" :alt="previewFile.name" class="preview-modal__image" />
      </div>
      <template #footer>
        <CButton color="secondary" @click="previewModal = false, previewFile = null">Close</CButton>
        <CButton color="info" v-if="previewFile" @click="downloadFile(previewFile)">Download</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
var methods = {}

methods.applySearch = function () {
  this.page = 1
  this.loadFolders()
}

methods.changePage = function (page) {
  this.page = page
  this.loadFolders()
}

methods.changeFileOffset = function (offset) {
  this.fileOffset = Math.max(offset, 0)
  if (this.currentFolder) {
    this.openFolder(this.currentFolder.id, false)
  }
}

methods.loadFolders = function () {
  this.foldersLoading = true
  axios.get(this.$apiAdress + '/api/customer-folder-portal?token=' + localStorage.getItem('api_token'), {
    params: {
      search: this.search,
      page: this.page,
      per_page: this.perPage
    }
  }).then(response => {
    this.folders = response.data.data || []
    this.meta = {
      current_page: response.data.current_page || 1,
      last_page: response.data.last_page || 1,
      total: response.data.total || 0
    }

    if (!this.currentFolder && this.folders.length) {
      this.openFolder(this.folders[0].id)
    }
  }).finally(() => {
    this.foldersLoading = false
  })
}

methods.openFolder = function (folderId, resetOffset = true) {
  if (resetOffset) {
    this.fileOffset = 0
  }

  this.folderLoading = true
  axios.get(this.$apiAdress + '/api/customer-folder-portal/folders/' + folderId + '?token=' + localStorage.getItem('api_token'), {
    params: {
      file_limit: this.fileLimit,
      file_offset: this.fileOffset
    }
  }).then(response => {
    this.currentFolder = response.data.folder
    this.breadcrumbs = response.data.breadcrumbs || []
    this.subfolders = response.data.subfolders || []
    this.files = response.data.files || []
    this.filesTotal = response.data.files_total || 0
    this.fileLimit = response.data.file_limit || this.fileLimit
    this.fileOffset = response.data.file_offset || 0
    this.loadExports()
  }).finally(() => {
    this.folderLoading = false
  })
}

methods.loadExports = function () {
  this.exportsLoading = true
  axios.get(this.$apiAdress + '/api/customer-folder-portal/exports?token=' + localStorage.getItem('api_token'), {
    params: {
      folder_id: this.currentFolder ? this.currentFolder.id : null,
      limit: 12,
      include_completed: this.showCompletedExports ? 1 : 0
    }
  }).then(response => {
    const exports = response.data || []
    this.exports = exports.length ? [exports[0]] : []
    this.syncExportPolling()
  }).finally(() => {
    this.exportsLoading = false
  })
}

methods.queueExport = function () {
  if (!this.currentFolder) {
    return
  }

  this.exportSubmitting = true
  this.showCompletedExports = true
  axios.post(this.$apiAdress + '/api/customer-folder-portal/exports?token=' + localStorage.getItem('api_token'), {
    folder_id: this.currentFolder.id,
    recursive: true
  }).then(() => {
    this.startExportPolling()
    this.loadExports()
  }).finally(() => {
    this.exportSubmitting = false
  })
}

methods.downloadExport = function (item) {
  window.open(this.$apiAdress + '/api/customer-folder-portal/exports/' + item.id + '/download?token=' + localStorage.getItem('api_token'), '_blank')
  this.exports = this.exports.filter(exportItem => exportItem.id !== item.id)
  if (!this.exports.length) {
    this.showCompletedExports = false
  }
  this.syncExportPolling()
}

methods.downloadFile = function (file) {
  if (!this.currentFolder) {
    return
  }

  window.open(this.$apiAdress + '/api/media/file/download?thisFolder=' + this.currentFolder.id + '&id=' + file.id + '&token=' + localStorage.getItem('api_token'), '_blank')
}

methods.previewSrc = function (file) {
  return this.$apiAdress + '/api/showThumbnail/' + file.id + '?token=' + localStorage.getItem('api_token')
}

methods.fullImageSrc = function (file) {
  return this.$apiAdress + '/api/showNewImage/' + file.id + '?token=' + localStorage.getItem('api_token')
}

methods.openPreview = function (file) {
  this.previewFile = file
  this.previewModal = true
}

methods.hasActiveExports = function () {
  return this.exports.some(item => item.status === 'queued' || item.status === 'running')
}

methods.startExportPolling = function () {
  if (this.exportPoller) {
    return
  }

  this.exportPoller = setInterval(() => {
    if (!this.currentFolder) {
      this.stopExportPolling()
      return
    }

    this.loadExports()
  }, 5000)
}

methods.stopExportPolling = function () {
  if (this.exportPoller) {
    clearInterval(this.exportPoller)
    this.exportPoller = null
  }
}

methods.syncExportPolling = function () {
  if (this.hasActiveExports()) {
    this.startExportPolling()
    return
  }

  this.stopExportPolling()

  if (!this.exports.some(item => item.status === 'completed')) {
    this.showCompletedExports = false
  }
}

methods.statusColor = function (status) {
  if (status === 'completed') {
    return 'success'
  }
  if (status === 'failed') {
    return 'danger'
  }
  if (status === 'running') {
    return 'warning'
  }
  return 'secondary'
}

methods.formatBytes = function (size) {
  if (!size && size !== 0) {
    return '—'
  }

  const units = ['B', 'KB', 'MB', 'GB']
  let value = Number(size)
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex++
  }

  return value.toFixed(unitIndex === 0 ? 0 : 1) + ' ' + units[unitIndex]
}

methods.formatDate = function (value) {
  if (!value) {
    return '—'
  }

  return new Date(value).toLocaleString()
}

export default {
  name: 'CustomerFolderPortal',
  data () {
    return {
      search: '',
      page: 1,
      perPage: 30,
      folders: [],
      foldersLoading: false,
      meta: {
        current_page: 1,
        last_page: 1,
        total: 0
      },
      currentFolder: null,
      folderLoading: false,
      breadcrumbs: [],
      subfolders: [],
      files: [],
      filesTotal: 0,
      fileLimit: 100,
      fileOffset: 0,
      previewModal: false,
      previewFile: null,
      exports: [],
      exportsLoading: false,
      exportSubmitting: false,
      exportPoller: null,
      showCompletedExports: false
    }
  },
  mounted () {
    this.loadFolders()
  },
  beforeDestroy () {
    this.stopExportPolling()
  },
  methods: methods
}
</script>

<style scoped>
.customer-folder-portal {
  width: 100%;
  float: left;
}

.portal-title {
  font-weight: 600;
}

.portal-breadcrumbs {
  margin-top: 4px;
  color: #768192;
}

.breadcrumb-button {
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
}

.folder-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.folder-list-item,
.content-folder {
  width: 100%;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  padding: 12px;
}

.folder-list-item.active {
  border-color: #321fdb;
  box-shadow: 0 0 0 1px rgba(50, 31, 219, 0.15);
}

.folder-list-item__name,
.content-folder__name,
.export-card__name {
  font-weight: 600;
}

.folder-list-item__meta,
.content-folder__meta,
.export-card__meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #768192;
  font-size: 12px;
  margin-top: 4px;
}

.portal-state,
.portal-empty {
  color: #768192;
  padding: 12px 0;
}

.folder-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.portal-actions {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 10px;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.content-folder__icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.exports-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.export-card {
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 12px;
}

.export-card__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.export-card__error {
  color: #e55353;
  margin: 8px 0;
  font-size: 12px;
}

.files-table {
  overflow-x: auto;
}

.file-preview img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #d8dbe0;
}

.preview-image {
  cursor: pointer;
}

.preview-modal__header {
  margin-bottom: 12px;
}

.preview-modal__image {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  background: #f8f9fa;
  border-radius: 8px;
}
</style>
